import { CheckCircle2, Loader2 } from "lucide-react";

import { type CarrierTripDetail, useCompleteCarrierTrip } from "@/api/carrier";
import { Button } from "@/components/ui/button";

export function TripCompletionAction({
  detail,
  reportSuccess,
  reportError,
}: {
  detail: CarrierTripDetail;
  reportSuccess: (message: string) => void;
  reportError: (error: unknown, fallback: string) => void;
}) {
  const completeTrip = useCompleteCarrierTrip();
  const canComplete =
    detail.status === "ACTIVE" &&
    detail.tasks.every(
      (task) =>
        ["COMPLETED", "FAILED", "CANCELLED"].includes(task.status) ||
        (task.status === "IN_PROGRESS" &&
          task.investigation?.sourceTripId === detail.id &&
          !!task.investigation.id &&
          ["NEEDS_INVESTIGATION", "CONFIRMED_LOST"].includes(
            task.investigation.state
          ))
    );

  const handleCompleteTrip = async () => {
    try {
      await completeTrip.mutateAsync({ tripId: detail.id });
      reportSuccess("Trip completed.");
    } catch (error) {
      reportError(error, "Trip could not be completed.");
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-8 gap-1.5 text-[12px]"
      onClick={handleCompleteTrip}
      disabled={!canComplete || completeTrip.isPending}
      // The reason it is shut, on the control itself, rather than as a line of
      // prose that has to sit somewhere in the dialog.
      title={
        detail.status === "ACTIVE" && !canComplete
          ? "Resolve executing work or save its supported investigation disposition before closing this Trip."
          : undefined
      }
    >
      {completeTrip.isPending ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <CheckCircle2 className="h-3.5 w-3.5" />
      )}
      Complete trip
    </Button>
  );
}
